import { createClient } from '@supabase/supabase-js';

// Accessing environment variables in a way compatible with both Vite (via define) and Node CJS (esbuild)
const getEnvVar = (key: 'VITE_SUPABASE_URL' | 'VITE_SUPABASE_ANON_KEY'): string => {
  if (typeof window !== 'undefined' && (window as any).__ENV__ && (window as any).__ENV__[key]) {
    return (window as any).__ENV__[key];
  }
  if (key === 'VITE_SUPABASE_URL' && typeof process !== 'undefined' && process.env?.VITE_SUPABASE_URL) {
    return process.env.VITE_SUPABASE_URL;
  }
  if (key === 'VITE_SUPABASE_ANON_KEY' && typeof process !== 'undefined' && process.env?.VITE_SUPABASE_ANON_KEY) {
    return process.env.VITE_SUPABASE_ANON_KEY;
  }
  if (typeof process !== 'undefined' && process.env && process.env[key]) {
    return process.env[key] as string;
  }
  return '';
};

const supabaseUrl = getEnvVar('VITE_SUPABASE_URL');
const supabaseAnonKey = getEnvVar('VITE_SUPABASE_ANON_KEY');

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.');
  
  // PWA Recovery: If variables are missing, it might be due to a stale Service Worker serving an old index.html.
  // Unregister all service workers and reload to get the latest index.html with /env.js
  if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(function(registrations) {
      let unregistered = false;
      for(let registration of registrations) {
        registration.unregister();
        unregistered = true;
      }
      if (unregistered) {
        console.log("Unregistered stale service workers. Reloading page...");
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    });
  }
}


const customFetch = async (url: RequestInfo | URL, options?: RequestInit): Promise<Response> => {
  let retries = 3;
  let lastError: any;
  for (let i = 0; i <= retries; i++) {
    try {
      const response = await fetch(url, options);
      if (!response.ok && response.status >= 500) {
        throw new Error(`Supabase server error: ${response.status}`);
      }
      return response;
    } catch (err: any) {
      lastError = err;
      const isTransient = err.message.includes('fetch') || err.message.includes('network') || err.message.includes('timeout') || err.message.includes('50');
      if (!isTransient || i === retries) {
        console.error(`Supabase fetch failed: ${url}`, err);
        throw err;
      }
      console.warn(`Supabase fetch retry ${i + 1}/${retries} for ${url}`);
      await new Promise(r => setTimeout(r, 1000 * Math.pow(2, i))); // exponential backoff
    }
  }
  throw lastError;
};

export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co', 
  supabaseAnonKey || 'placeholder',
  {
    global: {
      fetch: customFetch
    }
  }
);

